import type { ComputedRef, Ref, WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { CurriculumModuleOption } from './interfaces'
import type { DisciplineCode } from './types'
import type { AssignmentType, Grade } from './enums'

import { useSidebarPrivileges } from '@entities/viewer'
import { gradeOptions } from '@shared/lib/format'

import { useAssignmentTemplatesStore } from '../assignment-templates/store'
import { useAssignmentTemplatesApi } from '../../api/assignment-templates/list.hook'
import { useCurriculumModulesApi } from '../../api/curriculum/modules.hook'
import {
  assignmentTypeOptions,
  disciplineCodeOptions,
  getModuleOptions,
} from './assignment-template-options'

export const MODULE_LOCKED_PLACEHOLDER = 'Pick a grade and discipline'

interface FiltersAssignmentTemplatesReturn {
  refSearch: Ref<string>
  refType: Ref<AssignmentType | undefined>
  refDisciplineCode: WritableComputedRef<DisciplineCode | undefined>
  refGrade: WritableComputedRef<Grade | undefined>
  refModuleId: Ref<number | undefined>
  refModuleOptions: ComputedRef<AppSelectOption<number>[]>
  /** The module catalogue is per grade + discipline, so the select is locked until both are set. */
  refIsModuleLocked: ComputedRef<boolean>
  refIsModulesLoading: Ref<boolean>
  refModulePlaceholder: ComputedRef<string>
  refCanManage: ComputedRef<boolean>
  refIsLoading: ComputedRef<boolean>
  refHasFilters: ComputedRef<boolean>
  typeOptions: AppSelectOption<AssignmentType>[]
  disciplineOptions: AppSelectOption<DisciplineCode>[]
  gradeSelectOptions: AppSelectOption<Grade>[]
  openCreateModal: () => void
  submitSearch: () => Promise<void>
  clearFilters: () => Promise<void>
}

/** The controls hold a draft; nothing is requested until "Search" or Enter in the search input. */
export const useFiltersAssignmentTemplates = (): FiltersAssignmentTemplatesReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useAssignmentTemplatesStore()
  const { canManageAssignmentTemplates } = useSidebarPrivileges()

  const refModules = ref<CurriculumModuleOption[]>([])
  const refIsModulesLoading = ref<boolean>(false)
  const refSearch = ref<string>('')
  const refType = ref<AssignmentType | undefined>(undefined)
  const refDraftDisciplineCode = ref<DisciplineCode | undefined>(undefined)
  const refDraftGrade = ref<Grade | undefined>(undefined)
  const refModuleId = ref<number | undefined>(undefined)

  const syncDraft = (): void => {
    refSearch.value = store.getFilterSearch ?? ''
    refType.value = store.getFilterType ?? undefined
    refDraftDisciplineCode.value = store.getFilterDisciplineCode ?? undefined
    refDraftGrade.value = store.getFilterGrade ?? undefined
    refModuleId.value = store.getFilterModuleId ?? undefined
  }

  /** `page` is dropped so a new filter never lands on a page that no longer exists. */
  const syncQueryAndReload = async (): Promise<void> => {
    store.setCurrentPage(1)

    await router.push({
      query: {
        ...route.query,
        search: store.getFilterSearch || undefined,
        type: store.getFilterType || undefined,
        discipline_code: store.getFilterDisciplineCode || undefined,
        grade: store.getFilterGrade || undefined,
        curriculum_module_id: store.getFilterModuleId || undefined,
        page: undefined,
      },
    })
    await useAssignmentTemplatesApi()
  }

  // Module ids do not survive a grade or discipline change, so both setters drop the module.
  const refDisciplineCode = computed<DisciplineCode | undefined>({
    get: () => refDraftDisciplineCode.value,
    set: (value) => {
      refDraftDisciplineCode.value = value
      refModuleId.value = undefined
    },
  })

  const refGrade = computed<Grade | undefined>({
    get: () => refDraftGrade.value,
    set: (value) => {
      refDraftGrade.value = value
      refModuleId.value = undefined
    },
  })

  const refIsModuleLocked = computed<boolean>(() => !refGrade.value || !refDisciplineCode.value)

  const refModuleOptions = computed<AppSelectOption<number>[]>(() => getModuleOptions(refModules.value))

  const refModulePlaceholder = computed<string>(() =>
    refIsModuleLocked.value ? MODULE_LOCKED_PLACEHOLDER : 'All modules')

  const refCanManage = computed<boolean>(() => canManageAssignmentTemplates.value)
  const refIsLoading = computed<boolean>(() => store.isLoading)
  const refHasFilters = computed<boolean>(() => store.hasFilters)

  const loadModules = async (): Promise<void> => {
    const grade = refGrade.value
    const disciplineCode = refDisciplineCode.value

    refModules.value = []

    if (!grade || !disciplineCode) return

    const modules = await useCurriculumModulesApi({
      disciplineCode,
      grade,
      onStart: () => { refIsModulesLoading.value = true },
      onFinish: () => { refIsModulesLoading.value = false },
    })

    // The pair may have moved on while the request was in flight; only the current pair's answer lands.
    if (!modules || grade !== refGrade.value || disciplineCode !== refDisciplineCode.value) return

    refModules.value = modules
  }

  const openCreateModal = (): void => {
    store.setModalCreate(true)
  }

  const submitSearch = async (): Promise<void> => {
    store.setFilterSearch(refSearch.value.trim() || null)
    store.setFilterType(refType.value ?? null)
    store.setFilterDisciplineCode(refDisciplineCode.value ?? null)
    store.setFilterGrade(refGrade.value ?? null)
    store.setFilterModuleId(refModuleId.value ?? null)

    await syncQueryAndReload()
  }

  const clearFilters = async (): Promise<void> => {
    store.resetFilters()
    syncDraft()

    await syncQueryAndReload()
  }

  // The store, not the URL: the list hydrates it from the query, and the draft follows once it has.
  watch(
    () => [
      store.getFilterSearch,
      store.getFilterType,
      store.getFilterDisciplineCode,
      store.getFilterGrade,
      store.getFilterModuleId,
    ],
    syncDraft,
    { immediate: true, deep: true },
  )

  watch([refGrade, refDisciplineCode], () => { void loadModules() }, { immediate: true })

  return {
    refSearch,
    refType,
    refDisciplineCode,
    refGrade,
    refModuleId,
    refModuleOptions,
    refIsModuleLocked,
    refIsModulesLoading,
    refModulePlaceholder,
    refCanManage,
    refIsLoading,
    refHasFilters,
    typeOptions: assignmentTypeOptions,
    disciplineOptions: disciplineCodeOptions,
    gradeSelectOptions: gradeOptions,
    openCreateModal,
    submitSearch,
    clearFilters,
  }
}
